import { HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';


export interface CachedData {
  time: number;
  body: object & HttpResponse<any>;
}

type StorageKey = string;

@Injectable({
  providedIn: 'root'
})
export class CacheInterceptorControllerService {
  private canUseLocalStorage: Record<StorageKey, boolean> = {};
  private hasLocalStorage: boolean;
  private httpEvents: Record<StorageKey, BehaviorSubject<any>> = {};
  private httpMadeStory: Record<StorageKey, boolean> = {};
  private tempStorage: Record<StorageKey, string> = {};

  constructor() {
    this.checkLocalStorage();
  }

  public setCanUseLocalStorage(storageKey: StorageKey, isCan: boolean): void {
    this.canUseLocalStorage[storageKey] = isCan;
  }

  public checkIsHttpMade(storageKey: StorageKey): boolean {
    return !!this.httpMadeStory[storageKey];
  }

  public cleanHttpMadeStoryByKeys(storageKeys: (StorageKey | RegExp)[]): void {
    storageKeys.forEach(storageKey => {
      if (typeof storageKey === 'string') {
        if (this.httpMadeStory && this.httpMadeStory[storageKey]) {
          this.httpMadeStory[storageKey] = false;
        }

      } else {
        Object.keys(this.httpMadeStory).forEach(key => {
          if (storageKey.test(key)) {
            this.httpMadeStory[key] = false;
          }
        });
      }
    });
  }

  public getRequestKey(req: HttpRequest<any>): string {
    return req.body
      ? JSON.stringify(req.body)
      : req.urlWithParams || req.url;
  }

  public saveData(storageKey: StorageKey, body: any): void {
    const time = Date.now();
    const data: CachedData = { time, body };
    const cachedData = JSON.stringify(data);

    if (this.canUseLocalStorage[storageKey]) {
      this.saveDataInLocalStorage(storageKey, cachedData);
    } else {
      this.tempStorage[storageKey] = cachedData;
    }

    this.httpEvents[storageKey]?.next(body);
  }

  public setHttpStoryMade(storageKey: StorageKey): void {
    this.httpMadeStory[storageKey] = true;
  }

  public unsetHttpStoryMade(storageKey: StorageKey): void {
    delete this.httpMadeStory[storageKey];
  }

  public saveHttpEvent(storageKey: StorageKey, defaultValue?: CachedData['body']): void {
    this.httpEvents[storageKey] = new BehaviorSubject<any>(defaultValue);
  }

  public removeHttpEvent(storageKey: StorageKey, reason: any = 'Error'): void {
    if (!this.httpEvents[storageKey]) { return; }
    console.log('Http Event was stopped by', reason);
    delete this.httpEvents[storageKey];
  }

  public getHttpEvent(storageKey: StorageKey): BehaviorSubject<any> {
    return this.httpEvents[storageKey];
  }

  public getData(storageKey: StorageKey): CachedData['body'] {
    if (this.canUseLocalStorage[storageKey]) {
      const cachedData = this.getDataFromLocalStorage(storageKey);

      if (cachedData && cachedData.body) {
        return cachedData.body;
      }

    } else {
      const json = this.tempStorage[storageKey];

      if (json) {
        const cachedData = JSON.parse(this.tempStorage[storageKey]) as CachedData;

        if (cachedData && cachedData.body) {
          return cachedData.body;
        }
      }
    }
  }

  private checkLocalStorage(): void {
    try {
      const test = 't';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      this.hasLocalStorage = true;
    } catch (e) {
      this.hasLocalStorage = false;
    }
  }

  private saveDataInLocalStorage(storageKey: StorageKey, cachedData: any): void {
    if (!this.hasLocalStorage || !cachedData) { return; }

    localStorage.setItem(storageKey, cachedData);
  }

  private getDataFromLocalStorage(storageKey: StorageKey): CachedData {
    if (!this.hasLocalStorage) { return; }

    const cachedDataJson = localStorage.getItem(storageKey);

    if (cachedDataJson) {
      return JSON.parse(cachedDataJson) as CachedData;
    }
  }

}
